import { useState, useCallback, useMemo, useEffect } from "react";
import { MaterialCallout } from "../../../entities-generated/common/material-callout";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ImmutableFormValueService, ValidatedFormValueService, MutableFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged, isNotNullOrWhitespace } from "../../../framework/utils";
import { none, where } from "../../../framework/utils/collection";
import { IAssessmentLaborEditingModel, AssessmentLaborEditingModel } from "../entities/assess-rec-labor-fm";
import { ProjectEvents } from "../events";
import { IAssessmentRecorderLaborFormModel, IAssessmentRecorderLaborValueModel, IAssessmentRecorderLaborModelChange } from "./assess-record-labor-iface";
import { ILaborCallout } from "../../../entities-generated/common/labor-callout";


// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:isStandardLabor/immutable/boolean%%
export const isStandardLabor = new (class isStandardLaborService extends ImmutableFormValueService<IAssessmentLaborEditingModel, boolean> {
    public initialize(data: IAssessmentLaborEditingModel): void {
        this.standardLaborId = data.standardLaborId;
        this.value = isNotNullOrWhitespace(data.standardLaborId);
    }

    public applyToDataModel(data: IAssessmentLaborEditingModel): void {
        data.standardLaborId = this.standardLaborId;
    }

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public standardLaborId: string | undefined = undefined;

})(
    'isStandardLabor' // id
    ,'' // description
);
// %%END:isStandardLabor/immutable/boolean%%

// %%BEGIN:id/immutable/string%%
export const id = new (class idService extends ImmutableFormValueService<IAssessmentLaborEditingModel, string> {
    public initialize(data: IAssessmentLaborEditingModel): void {
        this.value = data.id;
    }

    public applyToDataModel(data: IAssessmentLaborEditingModel): void {
        data.id = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

})(
    'id' // id
    ,'' // description
);
// %%END:id/immutable/string%%

// %%BEGIN:description/validated/string/string%%
export const description = new (class descriptionService extends ValidatedFormValueService<IAssessmentLaborEditingModel, string, string> {
    public initialize(data: IAssessmentLaborEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.description));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentLaborEditingModel): void {
        data.description = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter the task description');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'description' // id
    ,'Task description' // description
);
// %%END:description/validated/string/string%%

// %%BEGIN:CustomerSummary/validated/string/string%%
export const CustomerSummary = new (class CustomerSummaryService extends ValidatedFormValueService<IAssessmentLaborEditingModel, string, string> {
    public initialize(data: IAssessmentLaborEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.customerSummary));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentLaborEditingModel): void {
        data.customerSummary = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'CustomerSummary' // id
    ,'Included in install explanation' // description
);
// %%END:CustomerSummary/validated/string/string%%

// %%BEGIN:time/validated/string/number%%
export const time = new (class timeService extends ValidatedFormValueService<IAssessmentLaborEditingModel, string, number> {
    public initialize(data: IAssessmentLaborEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.time > 0 ? data.time.toString() : ''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentLaborEditingModel): void {
        data.time = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const value = parseInt(this.value.value);
        return isNaN(value) === false ? value : 0;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter estimated work time');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'time' // id
    ,'Est. work time (blocks)' // description
);
// %%END:time/validated/string/number%%

// %%BEGIN:materials/mutable/MaterialCallout[]/MaterialCallout[]%%
export const materials = new (class materialsService extends MutableFormValueService<IAssessmentLaborEditingModel, MaterialCallout[], MaterialCallout[]> {
    public initialize(data: IAssessmentLaborEditingModel): void {
        this.value = data.materials;
    }

    public applyToDataModel(data: IAssessmentLaborEditingModel): void {
        data.materials = this.value;
    }

    public getDefaultValue(): MaterialCallout[] {
        return [];
    }

    public getInputValue(): MaterialCallout[] {
        return this.value;
    }

    public setInputValue(value: MaterialCallout[]): void {
        this.value = value;
    }

    public getOutputValue(): MaterialCallout[] {
        return this.value;
    }

    public onUpdate(value: MaterialCallout[]): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'materials' // id
    ,'Materials Needed' // description
);
// %%END:materials/mutable/MaterialCallout[]/MaterialCallout[]%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IAssessmentRecorderLaborFormModel => {
    return {
       isStandardLabor: isStandardLabor.getInputValue(),
       description: description.getInputValue(),
       CustomerSummary: CustomerSummary.getInputValue(),
       time: time.getInputValue(),
       materials: materials.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IAssessmentRecorderLaborValueModel => {
    return {
       id: id.getOutputValue(),
       description: description.getOutputValue(),
       CustomerSummary: CustomerSummary.getOutputValue(),
       time: time.getOutputValue(),
       materials: materials.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        description.getInputValue(),
        CustomerSummary.getInputValue(),
        time.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IAssessmentRecorderLaborModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(ProjectEvents.AssessRecordLaborModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IAssessmentRecorderLaborFormModel>> => {
    const promises = [
        description.formValidateAndUpdate(),
        CustomerSummary.formValidateAndUpdate(),
        time.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IAssessmentRecorderLaborFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IAssessmentRecorderLaborValueModel, dataModel: IAssessmentLaborEditingModel } = {
    dataModel: new AssessmentLaborEditingModel()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IAssessmentLaborEditingModel) => {
    const dataModel = data || new AssessmentLaborEditingModel();

    isStandardLabor.initialize(dataModel);
    id.initialize(dataModel);
    description.initialize(dataModel);
    CustomerSummary.initialize(dataModel);
    time.initialize(dataModel);
    materials.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IAssessmentRecorderLaborFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
description.setChangePublisher(broadcastChange);
CustomerSummary.setChangePublisher(broadcastChange);
time.setChangePublisher(broadcastChange);
materials.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new AssessmentLaborEditingModel();

    isStandardLabor.applyToDataModel(dataModel);
    id.applyToDataModel(dataModel);
    description.applyToDataModel(dataModel);
    CustomerSummary.applyToDataModel(dataModel);
    time.applyToDataModel(dataModel);
    materials.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IAssessmentRecorderLaborModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], ProjectEvents.AssessRecordLaborModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IAssessmentRecorderLaborFormModel => {
    const [getter, setter] = useState<IAssessmentRecorderLaborFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IAssessmentRecorderLaborModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IAssessmentRecorderLaborFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('AssessmentRecorderLaborFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('AssessmentRecorderLaborFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toLaborCallout = () => {
    const v = toValueModel();
    const request: ILaborCallout = {
        standardLaborId: isStandardLabor.standardLaborId,
        id: v.id,
        description: v.description,
        customerSummary: v.CustomerSummary,
        time: v.time,
        materials: v.materials
    };
    return request;
}

// ##END:DEVELOPERCODE##
